import { Crosshair, MapPin } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

type Coordinates = { latitude: number; longitude: number };

export function LocationPickerMap({ center, value, onChange }: { center: Coordinates; value: Coordinates | null; onChange: (coordinates: Coordinates) => void }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<import('maplibre-gl').Map | null>(null);
  const markerRef = useRef<import('maplibre-gl').Marker | null>(null);
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!containerRef.current) return;
    let disposed = false;
    let map: import('maplibre-gl').Map | undefined;
    let marker: import('maplibre-gl').Marker | undefined;
    void import('maplibre-gl').then(({ default: maplibregl }) => {
      if (disposed || !containerRef.current) return;
      map = new maplibregl.Map({
        container: containerRef.current,
        style: { version: 8, sources: { osm: { type: 'raster', tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'], tileSize: 256, attribution: '© OpenStreetMap contributors', maxzoom: 19 } }, layers: [{ id: 'osm', type: 'raster', source: 'osm' }] },
        center: value ? [value.longitude, value.latitude] : [center.longitude, center.latitude],
        zoom: value ? 17 : 14,
        attributionControl: false,
        cooperativeGestures: true,
        pitchWithRotate: false,
        dragRotate: false,
      });
      mapRef.current = map;
      map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
      map.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-right');

      const placeMarker = (coordinates: Coordinates) => {
        if (!map) return;
        if (!marker) {
          const element = document.createElement('div');
          element.className = 'hg-map-marker hostel location-picker-marker';
          element.innerHTML = '<span></span>';
          marker = new maplibregl.Marker({ element, anchor: 'bottom', draggable: true }).setLngLat([coordinates.longitude, coordinates.latitude]).addTo(map);
          markerRef.current = marker;
          marker.on('dragend', () => {
            const point = marker?.getLngLat();
            if (point) onChange({ latitude: point.lat, longitude: point.lng });
          });
        } else marker.setLngLat([coordinates.longitude, coordinates.latitude]);
      };
      if (value) placeMarker(value);
      map.on('click', event => {
        const coordinates = { latitude: event.lngLat.lat, longitude: event.lngLat.lng };
        placeMarker(coordinates);
        onChange(coordinates);
      });
      requestAnimationFrame(() => map?.resize());
    }).catch(() => setError('Map could not be loaded.'));
    return () => { disposed = true; markerRef.current = null; mapRef.current = null; map?.remove(); };
  }, [center.latitude, center.longitude]);

  useEffect(() => {
    if (!value || !mapRef.current) return;
    if (markerRef.current) {
      markerRef.current.setLngLat([value.longitude, value.latitude]);
      mapRef.current.easeTo({ center: [value.longitude, value.latitude], zoom: Math.max(mapRef.current.getZoom(), 16), duration: 450 });
      return;
    }
    void import('maplibre-gl').then(({ default: maplibregl }) => {
      if (!mapRef.current || markerRef.current) return;
      const element = document.createElement('div');
      element.className = 'hg-map-marker hostel location-picker-marker';
      element.innerHTML = '<span></span>';
      const marker = new maplibregl.Marker({ element, anchor: 'bottom', draggable: true }).setLngLat([value.longitude, value.latitude]).addTo(mapRef.current);
      marker.on('dragend', () => { const point = marker.getLngLat(); onChange({ latitude: point.lat, longitude: point.lng }); });
      markerRef.current = marker;
      mapRef.current.easeTo({ center: [value.longitude, value.latitude], zoom: 16, duration: 450 });
    });
  }, [value?.latitude, value?.longitude]);

  function useCurrentLocation() {
    if (!navigator.geolocation) { setError('Location is not available on this device.'); return; }
    setLocating(true); setError('');
    navigator.geolocation.getCurrentPosition(
      position => { onChange({ latitude: position.coords.latitude, longitude: position.coords.longitude }); setLocating(false); },
      () => { setError('Allow location access, or tap the map to place the pin.'); setLocating(false); },
      { enableHighAccuracy: true, timeout: 12000 },
    );
  }

  return <section className="rep-location-picker">
    <div className="rep-location-heading"><div><strong>Select the hostel entrance</strong><span>Tap the map, then drag the pin if needed.</span></div><button type="button" className="secondary-button" onClick={useCurrentLocation} disabled={locating}><Crosshair size={16}/>{locating ? 'Locating…' : 'Use my location'}</button></div>
    <div className="rep-location-map"><div ref={containerRef} className="rep-location-map-canvas" aria-label="Map for selecting the hostel location"/><div className="rep-location-map-tip"><MapPin size={15}/>{value ? 'Pin selected' : 'Tap to place pin'}</div></div>
    {value && <div className="rep-location-confirmed"><MapPin size={16}/><span><strong>Location selected</strong><small>{value.latitude.toFixed(6)}, {value.longitude.toFixed(6)}</small></span></div>}
    {error && <small className="rep-location-error">{error}</small>}
  </section>;
}
